import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { TASK_STATUS_LABEL } from "@/lib/taskStatus";
import type { DashboardWidgetRow } from "@shared/types";

const WIDGET_TYPES: { value: string; label: string }[] = [
  { value: "count", label: "Open-task count" },
  { value: "breakdown", label: "Status breakdown" },
  { value: "task_list", label: "Task list" },
];

function DashboardsView() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newShared, setNewShared] = useState(false);

  const listQ = useQuery({
    queryKey: ["dashboards"],
    queryFn: api.listDashboards,
  });
  const dashboards = listQ.data?.dashboards ?? [];
  const current =
    dashboards.find((d) => d.id === selected) ?? dashboards[0] ?? null;

  const create = useMutation({
    mutationFn: () => api.createDashboard(newName.trim(), newShared),
    onSuccess: (r) => {
      setNewName("");
      setNewShared(false);
      qc.invalidateQueries({ queryKey: ["dashboards"] });
      setSelected(r.dashboard.id);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteDashboard(id),
    onSuccess: () => {
      setSelected(null);
      qc.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });

  return (
    <div className="pg-wrap" style={{ padding: "22px 30px 60px" }}>
      <div className="rpt-head" style={{ marginBottom: 6 }}>
        <h1 className="pg-title" style={{ margin: 0 }}>
          Dashboards
        </h1>
      </div>

      <div className="dash-bar">
        <select
          className="select"
          style={{ width: 200 }}
          value={current?.id ?? ""}
          onChange={(e) => setSelected(e.target.value)}
          aria-label="Select dashboard"
        >
          {dashboards.length === 0 && <option value="">No dashboards</option>}
          {dashboards.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
              {d.shared ? " (shared)" : ""}
              {d.mine ? "" : " · others"}
            </option>
          ))}
        </select>
        <span style={{ flex: 1 }} />
        <input
          className="input"
          placeholder="New dashboard…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          aria-label="New dashboard name"
          style={{ width: 180 }}
        />
        <label className="t-sm" style={{ display: "flex", gap: 4 }}>
          <input
            type="checkbox"
            checked={newShared}
            onChange={(e) => setNewShared(e.target.checked)}
          />
          Shared
        </label>
        <Button
          variant="primary"
          size="sm"
          disabled={!newName.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          Create
        </Button>
      </div>

      {listQ.isLoading ? (
        <Skeleton count={3} height={120} />
      ) : !current ? (
        <EmptyState
          icon="grid"
          title="No dashboards yet"
          caption="Create a dashboard, then add widgets scoped to a project or the whole workspace."
        />
      ) : (
        <DashboardBody
          dashboard={current}
          onDelete={() => remove.mutate(current.id)}
        />
      )}
    </div>
  );
}

function DashboardBody({
  dashboard,
  onDelete,
}: {
  dashboard: { id: string; name: string; mine?: boolean };
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const widgetsQ = useQuery({
    queryKey: ["dashboard", dashboard.id],
    queryFn: () => api.getDashboard(dashboard.id),
  });
  const [wType, setWType] = useState("count");
  const [wScopeId, setWScopeId] = useState("");

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["dashboard", dashboard.id] });
  const addWidget = useMutation({
    mutationFn: () =>
      api.addWidget(dashboard.id, {
        type: wType,
        scopeKind: wScopeId ? "project" : "workspace",
        scopeId: wScopeId || null,
      }),
    onSuccess: invalidate,
  });
  const delWidget = useMutation({
    mutationFn: (id: string) => api.deleteWidget(id),
    onSuccess: invalidate,
  });

  const widgets = widgetsQ.data?.widgets ?? [];
  const projects = projectsQ.data?.projects ?? [];

  return (
    <div>
      {dashboard.mine !== false && (
        <div className="dash-bar" style={{ marginTop: 6 }}>
          <span className="t-sm t-muted">Add widget:</span>
          <select
            className="select"
            value={wType}
            onChange={(e) => setWType(e.target.value)}
            aria-label="Widget type"
          >
            {WIDGET_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={wScopeId}
            onChange={(e) => setWScopeId(e.target.value)}
            aria-label="Widget scope"
          >
            <option value="">Whole workspace</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            size="sm"
            disabled={addWidget.isPending}
            onClick={() => addWidget.mutate()}
          >
            Add
          </Button>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" onClick={onDelete}>
            Delete dashboard
          </Button>
        </div>
      )}

      {widgets.length === 0 ? (
        <p className="t-muted" style={{ marginTop: 16 }}>
          No widgets yet.
        </p>
      ) : (
        <div className="dashboards-grid">
          {widgets.map((w) => (
            <Widget
              key={w.id}
              widget={w}
              projectName={
                w.scopeId
                  ? (projects.find((p) => p.id === w.scopeId)?.name ??
                    "Project")
                  : "Workspace"
              }
              canDelete={dashboard.mine !== false}
              onDelete={() => delWidget.mutate(w.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Widget({
  widget,
  projectName,
  canDelete,
  onDelete,
}: {
  widget: DashboardWidgetRow;
  projectName: string;
  canDelete: boolean;
  onDelete: () => void;
}) {
  // Live data: project-scoped reads that project's tasks; workspace reads all
  // open tasks across the user's projects.
  const isProject = widget.scopeKind === "project" && widget.scopeId;
  const dataQ = useQuery({
    queryKey: isProject
      ? queryKeys.tasksFor(widget.scopeId!)
      : ["team", "tasks"],
    queryFn: async (): Promise<{
      tasks: Array<{ title: string; status: string }>;
    }> =>
      isProject ? await api.listTasks(widget.scopeId!) : await api.teamTasks(),
  });
  const tasks = useMemo(() => dataQ.data?.tasks ?? [], [dataQ.data]);
  const open = tasks.filter((t) => t.status !== "done");
  const typeLabel =
    WIDGET_TYPES.find((t) => t.value === widget.type)?.label ?? widget.type;

  return (
    <div className="dash-widget">
      <div className="dash-widget-head">
        <div className="dash-widget-titles">
          <span className="dash-widget-type">{typeLabel}</span>
          <span className="t-sm t-muted">{projectName}</span>
        </div>
        {canDelete && (
          <button
            type="button"
            className="dash-widget-x"
            aria-label="Remove widget"
            onClick={onDelete}
          >
            ×
          </button>
        )}
      </div>
      {dataQ.isLoading ? (
        <Skeleton count={2} height={18} />
      ) : widget.type === "count" ? (
        <div className="dash-num">
          <div className="dash-num-val">{open.length}</div>
          <div className="t-sm t-muted">open tasks</div>
        </div>
      ) : widget.type === "breakdown" ? (
        <ul className="dash-breakdown">
          {["backlog", "todo", "doing", "done"].map((s) => {
            const n = tasks.filter((t) => t.status === s).length;
            return (
              <li key={s}>
                <span>{TASK_STATUS_LABEL[s as "todo"] ?? s}</span>
                <span className="t-mono">{n}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="dash-tasklist">
          {open.slice(0, 6).map((t, i) => (
            <li key={i} className="t-sm">
              • {t.title}
            </li>
          ))}
          {open.length === 0 && (
            <li className="t-sm t-muted">No open tasks.</li>
          )}
        </ul>
      )}
    </div>
  );
}

export const Route = createFileRoute("/dashboards")({
  component: DashboardsView,
});
